<script setup>
import { computed, ref, watch } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { id, content, attachments, contentAttributes, messageType, inboxId } =
  useMessageContext();
import { useFunctionGetter } from 'dashboard/composables/store';
const inboxRef = useFunctionGetter('inboxes/getInbox', inboxId);
const showOriginalForDeleted = computed(() => {
  const inbox = inboxRef.value || {};
  // Default false: only marker unless explicitly enabled
  return inbox.show_deleted_message_placeholder === true;
});

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const isDeletedByMarker = computed(() => {
  const text = content.value || '';
  return typeof text === 'string' && text.trim().startsWith('⛔');
});

const renderContent = computed(() => {
  // Base content selection (original vs translation)
  let baseContent = content.value;
  if (renderOriginal.value) {
    baseContent = content.value;
  } else if (hasTranslations.value) {
    baseContent = translationContent.value;
  }

  // If message was deleted and setting disables showing original text,
  // only show the deleted marker (first line)
  const isDeleted = contentAttributes.value?.deleted || isDeletedByMarker.value;
  if (
    isDeleted &&
    !showOriginalForDeleted.value &&
    typeof baseContent === 'string'
  ) {
    return baseContent.split(/\r?\n/)[0];
  }

  return baseContent;
});

// Force re-render when toggle or content state changes
const rerenderKey = ref(0);
watch(
  [
    showOriginalForDeleted,
    () => content.value,
    () => contentAttributes.value?.deleted,
  ],
  () => {
    rerenderKey.value += 1;
  }
);

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    :key="`text-${id}-${rerenderKey}`"
    class="px-4 py-3"
    data-bubble-name="text"
  >
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <FormattedContent
        v-if="renderContent"
        :key="`${id}-${rerenderKey}`"
        :content="renderContent"
      />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
